import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatNavList } from '@angular/material/list';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../../../shared/services/auth.service';
import { LayoutService } from '../../../shared/services/layout.service';
import { PendingProfileChangesService } from '../../../shared/services/pending-profile-changes.service';
import { SelectedProfileService } from '../../../shared/services/selected-profile.service';
import { UserPermission } from '../../../state/auth-model';
import { ConsumerProfileService } from '../../profile/services/profile.service';
import { AccessRequestService } from '../../services/access-request-service';

@Component({
  selector: 'app-left-nav-panel',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, MatNavList, MatCard, MatIconModule, MatCardContent],
  templateUrl: './left-nav-panel.component.html',
  styleUrls: ['./left-nav-panel.component.scss'],
})
export class LeftNavPanelComponent implements OnInit {
  private authService = inject(AuthService);
  selectedProfileService = inject(SelectedProfileService);
  consumerProfileService = inject(ConsumerProfileService);
  pendingProfileChangesService = inject(PendingProfileChangesService);
  accessRequestService = inject(AccessRequestService);
  isAuthenticated = this.authService.isAuthenticated;
  isMobile = inject(LayoutService).isMobile;

  menuItems = computed(() => {
    const isBusinessPartner = this.authService.isBusinessPartner();
    const isAdmin = this.authService.isAdmin();
    const isCwopa = this.authService.isCwopa();
    const isCitizen = this.authService.isCitizen();

    const profileId = isCitizen ? this.selectedProfileService.selectedRecipientId() : null;
    const isProfileVisible = isCitizen && profileId !== null && profileId !== undefined;
    const pendingCount = this.pendingProfileChangesService.pendingProfilesCount();
    const requestCount = this.pendingProfileChangesService.accessRequestCount();

    const isPrvdr =isAdmin ? true : this.authService.hasPermission(UserPermission.ProviderList);

    return [
      { label: 'Dashboard', route: '/dashboard', icon: 'home', visible: !isCwopa },
      { label: 'Create Profile', route: '/dashboard/create-profile', icon: 'person_add', visible: isBusinessPartner },
      { label: 'Create Reimbursement Request', route: '/agent-trip-request', icon: 'payments', visible: isBusinessPartner },
      { label: `Pending Profile Updates (${pendingCount})`, route: '/dashboard/pending-profile-updates', icon: 'pending_actions', visible: isBusinessPartner },
      { label: `Access Requests (${requestCount})`, route: '/dashboard/access-requests', icon: 'vpn_key', visible: isBusinessPartner },
      { label: 'Role Access Management', route: '/dashboard/roles-access', icon: 'admin_panel_settings', visible: isAdmin },
      { label: 'Additional Review Queue Rules', route: '/dashboard/additional-review-queue-rules', icon: 'admin_panel_settings', visible: isAdmin },
      { label: 'Frequent Provider List', route: '/dashboard/frequent-provider-list', icon: 'medical_services', visible: isPrvdr},
      { label: 'Advanced Search', route: '/dashboard/advance-search', icon: 'search', visible: isBusinessPartner || isCwopa },
      { label: 'User Profile', route: `/dashboard/profile/${profileId}`, icon: 'person', visible: isProfileVisible },
      { label: 'Help', route: '/help', icon: 'help_outline', visible: !isCwopa },
    ];
  });

  ngOnInit() {
    if (this.authService.isBusinessPartner()) {
      this.getPendingProfilesCount();
      this.getAccessRequestCount();
    }
  }

  getPendingProfilesCount() {
    this.consumerProfileService.getPendingProfilesWithPendingChangesCount().subscribe((count) => {
      this.pendingProfileChangesService.pendingProfilesCount.set(count);
    });
  }

  getAccessRequestCount() {
    this.accessRequestService.GetRequestCountByCounty().subscribe((count) => {
      this.pendingProfileChangesService.accessRequestCount.set(count);
    });
  }

  onLogin(event: Event, role: 'BusinessPartner' | 'Citizen' | 'CWOPA') {
    event.preventDefault();
    this.authService.login(role);
  }

  onLogout(event: Event) {
    event.preventDefault();
    this.authService.logout();
  }
}
